import Sentry from '@sentry/react-native';
import {
  Header,
  LogoIcon,
  Modal,
  TextBold,
  TextReg,
  isNative,
  useAppState,
  useAuthStore,
  useBanner,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import * as Application from 'expo-application';
import * as Updates from 'expo-updates';
import { DateTime } from 'luxon';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

export function UpdateCheckGuard({ children }: PropsWithChildren) {
  const { appState } = useAppState();
  const { showBanner } = useBanner();
  const { currentUserId, currentUserRole } = useAuthStore();
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const now = DateTime.now();
  const lastCheckedRef = useRef(now.minus({ hours: 2 }));
  const styles = useDynamicStyles(createStyles);
  const errorContext = {
    isEmergencyLaunch: Updates.isEmergencyLaunch,
    updateId: Updates.updateId,
    channel: Updates.channel,
    runtimeVersion: Updates.runtimeVersion,
    nativeApplicationVersion: Application.nativeApplicationVersion,
    user: {
      userId: currentUserId || 'anonymous',
      userRole: currentUserRole,
    },
  };

  useEffect(() => {
    // Only check for updates in production builds on mobile
    if (__DEV__ || !isNative) {
      return;
    }

    if (Updates.isEmergencyLaunch) {
      Sentry?.captureMessage('Emergency launch detected', {
        level: 'error',
        contexts: {
          'expo-updates': errorContext,
        },
      });
      setIsErrorModalVisible(true);
      return;
    }

    async function onFetchUpdateAsync() {
      const update = await Updates.checkForUpdateAsync();

      if (update.isAvailable) {
        await Updates.fetchUpdateAsync();
        showBanner({
          text: 'New update downloaded! Reload to continue',
          isBlocking: true,
          action: {
            label: 'Reload',
            onPress: async () => {
              await Updates.reloadAsync();
            },
          },
        });
      }
    }

    if (
      appState === 'active' &&
      now > lastCheckedRef.current.plus({ hours: 1 })
    ) {
      lastCheckedRef.current = now;
      void onFetchUpdateAsync().catch((err) => {
        Sentry?.withScope((scope) => {
          scope.setContext('expo-updates', errorContext);
          Sentry?.captureException(err);
        });
        setIsErrorModalVisible(true);
      });
    }
  }, [appState, showBanner]);

  return (
    <>
      {children}
      <Modal
        visible={isErrorModalVisible}
        hide={() => {}}
        header={<Header mobileLogo={LogoIcon} />}
      >
        <View style={styles.container}>
          <TextBold style={styles.title}>Oh no!</TextBold>

          <TextReg style={styles.message}>An update failed to apply</TextReg>
          <TextReg style={styles.message}>
            Please check your internet connection and relaunch the app
          </TextReg>
        </View>
      </Modal>
    </>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 32,
      flex: 1,
      paddingBottom: 75,
    },
    title: {
      fontSize: 26,
      textAlign: 'center',
    },
    message: {
      fontSize: 18,
      textAlign: 'center',
    },
  });
